/**
 * React Query hooks for the WorkWise parts of the New Deployment wizard.
 */
import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query'
import { toAzureLocation } from '@/config/azureRegions'
import { workwiseApi } from '@/services/workwise'
import type { CostEstimateRequest, SharedServerRole, WorkwiseEnvironment, WorkwiseFormValues } from '@/types/workwise'
import { parseApiError } from '@/utils/apiError'
import {
  COST_ESTIMATE_DEBOUNCE_MS,
  COST_ESTIMATE_STALE_MS,
  type CostEstimateState,
  costEstimateRequest,
} from '@/utils/workwise/costEstimate'
import { type ResolvedNetwork, formNetwork } from '@/utils/workwise/model'
import { isValidCustomerNumber } from '@/utils/workwise/naming'
import { useDebouncedValue } from './useDebouncedValue'

export const workwiseKeys = {
  all: ['workwise'] as const,
  sharedServers: (role: SharedServerRole, region: string, environment: WorkwiseEnvironment) =>
    [...workwiseKeys.all, 'shared-servers', role, region, environment] as const,
  existingNetworks: (region: string, environment: WorkwiseEnvironment) =>
    [...workwiseKeys.all, 'existing-networks', region, environment] as const,
  availability: (number: string, environment: WorkwiseEnvironment) =>
    [...workwiseKeys.all, 'availability', environment, number] as const,
  versions: () => [...workwiseKeys.all, 'versions'] as const,
  vmNames: (environment: WorkwiseEnvironment, customerNumber: string) =>
    [...workwiseKeys.all, 'vm-names', environment, customerNumber] as const,
  /** Revision 7: per-size VM compute prices of a region (location code). */
  sizePrices: (region: string) => [...workwiseKeys.all, 'size-prices', region] as const,
  /** Revision 7: keyed by the JSON of the request body, i.e. by the inputs that change the price. */
  costEstimate: (requestKey: string) => [...workwiseKeys.all, 'cost-estimate', requestKey] as const,
}

/**
 * Revision 7 (R7-B): VM compute per month and max users of every WorkWise size, for the region
 * (display name) shown by the wizard. The backend caches the retail prices for 24 h.
 */
export function useSizePrices(regionDisplay: string, enabled = true) {
  const region = toAzureLocation(regionDisplay)
  return useQuery({
    queryKey: workwiseKeys.sizePrices(region),
    queryFn: async () => (await workwiseApi.getSizePrices(region)).data,
    enabled: enabled && Boolean(region),
    staleTime: 60 * 60_000,
    retry: 1,
  })
}

/** The cost-estimate query for one request (its JSON), shared by the hook and the create call. */
export function costEstimateQuery(requestKey: string) {
  return queryOptions({
    queryKey: workwiseKeys.costEstimate(requestKey),
    queryFn: async () => (await workwiseApi.estimateCost(JSON.parse(requestKey) as CostEstimateRequest)).data,
    staleTime: COST_ESTIMATE_STALE_MS,
    retry: 1,
  })
}

/** The request body of the estimate for the form values, as its query key ('' when there is nothing to price). */
export function costEstimateKey(values: WorkwiseFormValues): string {
  const request = costEstimateRequest(values)
  return request ? JSON.stringify(request) : ''
}

/**
 * Revision 7 (R7-C): POST /workwise/cost-estimate/ for the form values, debounced and keyed by the
 * inputs that change the price. A failure never blocks the wizard: the caller shows "Price unavailable".
 */
export function useCostEstimate(values: WorkwiseFormValues, enabled = true): CostEstimateState {
  const key = costEstimateKey(values)
  const debouncedKey = useDebouncedValue(key, COST_ESTIMATE_DEBOUNCE_MS)
  const query = useQuery({
    ...costEstimateQuery(debouncedKey),
    enabled: enabled && debouncedKey !== '',
    placeholderData: keepPreviousData,
  })
  if (!enabled || key === '') return { status: 'idle', updating: false }
  const waiting = debouncedKey !== key
  if (query.data) return { status: 'ready', estimate: query.data, updating: waiting || query.isPlaceholderData || query.isFetching }
  if (query.isError && !waiting && !query.isFetching) return { status: 'error', updating: false }
  return { status: 'loading', updating: true }
}

/** Shared servers for a role, filtered server-side by region (display name) and environment. */
export function useSharedServers(
  role: SharedServerRole,
  regionDisplay: string,
  environment: WorkwiseEnvironment,
  enabled = true,
) {
  const region = toAzureLocation(regionDisplay)
  return useQuery({
    queryKey: workwiseKeys.sharedServers(role, region, environment),
    queryFn: async () => (await workwiseApi.getSharedServers({ role, region, environment })).data,
    enabled: enabled && Boolean(region),
    staleTime: 15_000,
    retry: 1,
  })
}

/**
 * Revision 4 (R4-B): the existing networks for the region (display name) and environment that a
 * SaaS-Hybrid app server can join; active only, filtered server-side.
 */
export function useExistingNetworks(regionDisplay: string, environment: WorkwiseEnvironment, enabled = true) {
  const region = toAzureLocation(regionDisplay)
  return useQuery({
    queryKey: workwiseKeys.existingNetworks(region, environment),
    queryFn: async () => (await workwiseApi.getExistingNetworks({ region, environment })).data,
    enabled: enabled && Boolean(region),
    staleTime: 30_000,
    retry: 1,
  })
}

/** The shared servers and the existing network currently picked in the form, resolved from the cached lists. */
export function useSharedPicks(values: WorkwiseFormValues, enabled = true) {
  const db = useSharedServers('db', values.region, values.environment, enabled)
  const gateway = useSharedServers('gateway', values.region, values.environment, enabled)
  const networks = useExistingNetworks(values.region, values.environment, enabled)
  const networkId = values.network?.existingNetworkId
  return {
    db: db.data?.find((s) => s.id === values.dbServer.sharedServerId),
    gateway: gateway.data?.find((s) => s.id === values.gatewayServer.sharedServerId),
    network: networkId == null ? undefined : networks.data?.find((n) => n.id === networkId),
  }
}

/**
 * The network the request will get (read-only, revisions 3 and 4): Dedicated derives
 * rg-/vnet-/snet-<region>-<env>-ww-<customer> and carries the typed address ranges;
 * SaaS-Hybrid takes the chosen existing network, else the picked shared DB server's,
 * or reports it pending when that server has none recorded.
 */
export function useWorkwiseNetwork(values: WorkwiseFormValues): ResolvedNetwork {
  return formNetwork(values, useSharedPicks(values))
}

export function useWorkwiseVersions(enabled = true) {
  return useQuery({
    queryKey: workwiseKeys.versions(),
    queryFn: async () => (await workwiseApi.getVersions()).data,
    enabled,
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

/**
 * Next free VM names per role. `customerNumber` is sent only when it is a valid
 * 3-digit number; without it the app and db `next` are null.
 */
export function useVmNames(environment: WorkwiseEnvironment, customerNumber: string, enabled = true) {
  const number = isValidCustomerNumber(customerNumber) ? customerNumber : ''
  return useQuery({
    queryKey: workwiseKeys.vmNames(environment, number),
    queryFn: async () => (await workwiseApi.getVmNames(environment, number || undefined)).data,
    enabled,
    staleTime: 10_000,
    retry: 1,
  })
}

export type AvailabilityStatus = 'idle' | 'checking' | 'available' | 'taken' | 'error'

export interface AvailabilityState {
  status: AvailabilityStatus
  message?: string
}

/**
 * Debounced availability check for the customer number (per environment). Idle
 * until the number is a complete, valid 3-digit value. A failed check ('error')
 * does not block the wizard: the server re-checks uniqueness on create.
 */
export function useCustomerAvailability(
  number: string,
  environment: WorkwiseEnvironment,
  enabled = true,
): AvailabilityState {
  const debounced = useDebouncedValue(number, 400)
  const valid = isValidCustomerNumber(number)
  const query = useQuery({
    queryKey: workwiseKeys.availability(debounced, environment),
    queryFn: async () => (await workwiseApi.checkCustomerNumber(debounced, environment)).data,
    enabled: enabled && isValidCustomerNumber(debounced),
    staleTime: 5_000,
    retry: false,
  })

  if (!enabled || !valid) return { status: 'idle' }
  if (debounced !== number) return { status: 'checking' }
  if (query.isError) return { status: 'error', message: parseApiError(query.error).message }
  if (query.isPending) return { status: 'checking' }
  if (query.data.available === false) {
    return { status: 'taken', message: `Customer number ${number} is already in use in this environment.` }
  }
  return { status: 'available' }
}
